import React from "react";
import { TaxiServiceName, TaxiServiceEmail, TaxiServiceDelete } from "../utils/languageMap"

// PrivacyPolicyComponent.jsx
// A simple static React component (Tailwind) that renders a Privacy Policy page without collapsible sections.

export default function PrivacyPolicy() {
  return (
    <div className="max-w-4xl mx-auto p-6 sm:p-10 bg-white rounded-2xl shadow-md" style={{ marginLeft: 50, marginRight: 50, marginBottom: 50 }}>
      <header className="mb-6">
        <h1 className="text-3xl sm:text-4xl font-semibold">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mt-1">Effective date: November 02, 2018</p>
        <p className="mt-4 text-gray-700 whitespace-pre-line">
          {TaxiServiceName} ("us", "we", or "our") operates a mobile application (hereinafter referred to as the "Service").
          {"\n\n"}
          This page informs you of our policies regarding the collection, use, and disclosure of personal data when you use our Service and the choices you have associated with that data. Our Privacy Policy for an app is managed with the help of Privacy Policies.
          {"\n\n"}
          We use your data to provide and improve the Service. By using the Service, you agree to the collection and use of information in accordance with this policy. Unless otherwise defined in this Privacy Policy, the terms used in this Privacy Policy have the same meanings as in our Terms and Conditions.
        </p>
      </header>

      <section className="space-y-6 text-gray-700 text-sm whitespace-pre-line">
        <div>
          <h2 className="text-lg font-medium">Information Collection And Use</h2>
          We collect several different types of information for various purposes to provide and improve our Service to you.
        </div>

        <div>
          <h2 className="text-lg font-medium">Types of Data Collected</h2>
          Personal Data
          While using our Service, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you ("Personal Data"). Personally identifiable information may include, but is not limited to:
          - First name and last name
          - Phone number
          - Cookies and Usage Data

          Usage Data
          When you access the Service with a mobile device, we may collect certain information automatically, including, but not limited to, the type of mobile device you use, your mobile device unique ID, the IP address of your mobile device, your mobile operating system, the type of mobile Internet browser you use, unique device identifiers and other diagnostic data ("Usage Data").

          Tracking & Cookies Data
          We use cookies and similar tracking technologies to track the activity on our Service and hold certain information.

          Examples of Cookies we use:
          - Session Cookies. We use Session Cookies to operate our Service.
          - Preference Cookies. We use Preference Cookies to remember your preferences and various settings.
          - Security Cookies. We use Security Cookies for security purposes.
        </div>

        <div>
          <h2 className="text-lg font-medium">Use of Data</h2>

          {TaxiServiceName} uses the collected data for various purposes:
          - To provide and maintain the Service
          - To notify you about changes to our Service
          - To allow you to participate in interactive features of our Service when you choose to do so
          - To provide customer care and support
          - To provide analysis or valuable information so that we can improve the Service
          - To monitor the usage of the Service
          - To detect, prevent and address technical issues
        </div>

        <div>
          <h2 className="text-lg font-medium">Transfer Of Data</h2>
          Your information, including Personal Data, may be transferred to — and maintained on — computers located outside of your state, province, country or other governmental jurisdiction where the data protection laws may differ than those from your jurisdiction.

          If you are located outside Ukraine and choose to provide information to us, please note that we transfer the data, including Personal Data, to Ukraine and process it there.

          Your consent to this Privacy Policy followed by your submission of such information represents your agreement to that transfer.
        </div>

        <div>
          <h2 className="text-lg font-medium">Disclosure Of Data</h2>
          Legal Requirements

          {TaxiServiceName} may disclose your Personal Data in the good faith belief that such action is necessary to:
          - To comply with a legal obligation
          - To protect and defend the rights or property of
          {TaxiServiceName}
          - To prevent or investigate possible wrongdoing in connection with the Service
          - To protect the personal safety of users of the Service or the public
          - To protect against legal liability
        </div>

        <div>
          <h2 className="text-lg font-medium">Security Of Data</h2>
          The security of your data is important to us, but remember that no method of transmission over the Internet, or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your Personal Data, we cannot guarantee its absolute security.
        </div>

        <div>
          <h2 className="text-lg font-medium">Data Retention and Deletion</h2>

          We retain personal data only for as long as necessary to provide our Service, comply with legal obligations, resolve disputes, and enforce our agreements.

          {"\n\n"}
          Retention periods depend on the type of data:
          {"\n"}
          - Account data is stored while your account remains active.
          {"\n"}
          - Usage and technical data is retained for a limited period to improve service performance and functionality.
          {"\n"}
          - Certain data may be retained longer if required by applicable laws.

          {"\n\n"}
          You have the right to request deletion of your personal data at any time. You can do this by:
          {"\n"}
          - Using the account deletion feature in the application (if available),
          {"\n"}
          - Visiting: {TaxiServiceDelete},
          {"\n"}
          - Or contacting us via email:{" "}
          <a
            href={`mailto:${TaxiServiceEmail}`}
            className="text-blue-600 hover:underline"
          >
            {TaxiServiceEmail}
          </a>

          {"\n\n"}
          Upon receiving a valid request, we will delete or anonymize your personal data within a reasonable timeframe, unless retention is required by law.
        </div>

        <div>
          <h2 className="text-lg font-medium">Service Providers</h2>
          We may employ third party companies and individuals to facilitate our Service ("Service Providers"), to provide the Service on our behalf, to perform Service-related services or to assist us in analyzing how our Service is used.

          These third parties have access to your Personal Data only to perform these tasks on our behalf and are obligated not to disclose or use it for any other purpose.
        </div>

        <div>
          <h2 className="text-lg font-medium">Links To Other Sites</h2>
          Our Service may contain links to other sites that are not operated by us. If you click on a third party link, you will be directed to that third party's site. We strongly advise you to review the Privacy Policy of every site you visit.

          We have no control over and assume no responsibility for the content, privacy policies or practices of any third party sites or services.
        </div>

        <div>
          <h2 className="text-lg font-medium">Children's Privacy</h2>
          Our Service does not address anyone under the age of 18 ("Children").

          We do not knowingly collect personally identifiable information from anyone under the age of 18. If you are a parent or guardian and you are aware that your Children has provided us with Personal Data, please contact us. If we become aware that we have collected Personal Data from children without verification of parental consent, we take steps to remove that information from our servers.
        </div>

        <div>
          <h2 className="text-lg font-medium">Changes To This Privacy Policy</h2>
          We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.

          We will let you know via email and/or a prominent notice on our Service, prior to the change becoming effective and update the "effective date" at the top of this Privacy Policy.

          Changes to this Privacy Policy are effective when they are posted on this page.
        </div>

        <div style={{ marginBottom: 50 }}>
          <h2 className="text-lg font-medium">Contact Us</h2>
          <p>If you have any questions about this Privacy Policy, please contact us:</p>
          <p>
            By email:{" "}
            <a
              href={`mailto:${TaxiServiceEmail}`}
              className="text-blue-600 hover:underline"
            >
              {TaxiServiceEmail}
            </a>
          </p>
        </div>
      </section>
    </div>
  );
}
